(() => {
  const footerSelector = '#main-cart-footer';
  const summarySelector = '.cart__blocks';
  let queued = false;

  const normalise = (value) => (value || '').replace(/\s+/g, ' ').trim().toLowerCase();

  const queueEnhancement = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      enhanceCartPageWidget();
    });
  };

  const isEligibleWidgetRoot = (element) => {
    if (!element) return false;
    return !element.matches(
      '.cart__blocks, .js-contents, .cart__ctas, .cart__dynamic-checkout-buttons, .irie-cart-page__summary-heading, .irie-cart-page__delivery-intro, script, style, noscript'
    );
  };

  const getWidgetBoundary = (element, summary) => {
    if (!element || !summary.contains(element)) return null;

    let root = element;
    while (
      root.parentElement &&
      root.parentElement !== summary &&
      !root.parentElement.matches('.cart__ctas')
    ) {
      root = root.parentElement;
    }

    if (root.parentElement === summary || root.parentElement?.matches('.cart__ctas')) return root;
    return null;
  };

  const findPickeasyWidgetRoot = (summary) => {
    const knownSelectors = [
      '[id*="pickeasy" i]',
      '[class*="pickeasy" i]',
      '[id*="logbase" i]',
      '[class*="logbase" i]',
      '[id*="pickup-delivery" i]',
      '[class*="pickup-delivery" i]',
      '[id*="delivery-date" i]',
      '[class*="delivery-date" i]',
    ];

    for (const selector of knownSelectors) {
      const match = summary.querySelector(selector);
      const root = getWidgetBoundary(match, summary);
      if (isEligibleWidgetRoot(root)) return root;
    }

    const markers = [
      'store pickup',
      'standard shipping',
      'when would you like to get this order',
      'additional details',
      'select date',
    ];

    const candidates = Array.from(summary.querySelectorAll('div, section, form, fieldset'))
      .filter(isEligibleWidgetRoot)
      .map((element) => {
        const text = normalise(element.textContent);
        const score = markers.reduce((total, marker) => total + (text.includes(marker) ? 1 : 0), 0);
        return { element, score, textLength: text.length };
      })
      .filter((candidate) => candidate.score >= 2)
      .sort((a, b) => b.score - a.score || a.textLength - b.textLength);

    for (const candidate of candidates) {
      const root = getWidgetBoundary(candidate.element, summary);
      if (isEligibleWidgetRoot(root)) return root;
    }

    return null;
  };

  const findTextElement = (widgetRoot, text) => {
    const target = normalise(text);
    const candidates = Array.from(
      widgetRoot.querySelectorAll('button, [role="button"], label, div, span, p, strong, b')
    )
      .filter((element) => {
        const content = normalise(element.textContent);
        return content === target || (content.includes(target) && content.length <= target.length + 36);
      })
      .sort((a, b) => {
        const interactiveA = a.matches('button, [role="button"], label') ? 0 : 1;
        const interactiveB = b.matches('button, [role="button"], label') ? 0 : 1;
        return interactiveA - interactiveB || normalise(a.textContent).length - normalise(b.textContent).length;
      });

    return candidates[0] || null;
  };

  const getControl = (widgetRoot, element) => {
    if (!element) return null;
    const interactive = element.closest('button, [role="button"], label');
    if (interactive && widgetRoot.contains(interactive)) return interactive;

    const parent = element.parentElement;
    if (parent && parent !== widgetRoot && parent.children.length <= 4) return parent;
    return element;
  };

  const decoratePickeasyWidget = (widgetRoot) => {
    widgetRoot.classList.add('irie-cart-page-pickeasy');
    widgetRoot.setAttribute('data-irie-cart-page-widget', 'pickeasy');
    widgetRoot.setAttribute('role', widgetRoot.getAttribute('role') || 'region');
    widgetRoot.setAttribute('aria-label', widgetRoot.getAttribute('aria-label') || 'Delivery or collection options');

    /*
     * Pickeasy can render different internal markup for pickup and shipping.
     * Earlier cart-page styling tried to turn detected internal wrappers into
     * a custom two-column grid. That was too invasive and could collapse the
     * app's own mobile layout into narrow columns. Remove only our old layout
     * hooks and let the app keep complete ownership of its method selector.
     */
    widgetRoot.querySelectorAll('.irie-pickeasy-methods').forEach((element) => {
      element.classList.remove('irie-pickeasy-methods');
    });
    widgetRoot.querySelectorAll('.irie-pickeasy-method').forEach((element) => {
      element.classList.remove('irie-pickeasy-method', 'is-selected');
    });

    /* Remove stale helper classes before re-detecting fields after an app refresh. */
    widgetRoot.querySelectorAll(
      '.irie-pickeasy-location, .irie-pickeasy-question, .irie-pickeasy-additional-label, .irie-pickeasy-date-field, .irie-pickeasy-notes-field'
    ).forEach((element) => {
      element.classList.remove(
        'irie-pickeasy-location',
        'irie-pickeasy-question',
        'irie-pickeasy-additional-label',
        'irie-pickeasy-date-field',
        'irie-pickeasy-notes-field'
      );
    });

    const addressMarker = findTextElement(widgetRoot, 'South Woodford') || findTextElement(widgetRoot, 'E18 1AY');
    const addressCard = getControl(widgetRoot, addressMarker);
    if (addressCard && addressCard !== widgetRoot) addressCard.classList.add('irie-pickeasy-location');

    findTextElement(widgetRoot, 'When would you like to get this order?')?.classList.add('irie-pickeasy-question');
    findTextElement(widgetRoot, 'Additional Details')?.classList.add('irie-pickeasy-additional-label');

    widgetRoot.querySelectorAll('input, select').forEach((field) => {
      const placeholder = normalise(field.getAttribute('placeholder'));
      if (field.type === 'date' || placeholder.includes('select date') || placeholder.includes('choose date')) {
        field.classList.add('irie-pickeasy-date-field');
      }
    });

    widgetRoot.querySelectorAll('textarea').forEach((field) => field.classList.add('irie-pickeasy-notes-field'));
  };

  const createDeliveryIntro = () => {
    const intro = document.createElement('div');
    intro.id = 'IrieCartPageDeliveryIntro';
    intro.className = 'irie-cart-page__delivery-intro';
    intro.innerHTML = `
      <span class="irie-cart-page__delivery-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 6h11v10H3z"></path>
          <path d="M14 9h3l4 4v3h-7z"></path>
          <circle cx="7" cy="18" r="2"></circle>
          <circle cx="18" cy="18" r="2"></circle>
        </svg>
      </span>
      <div>
        <p class="irie-cart-page__delivery-eyebrow">Delivery or collection</p>
        <h3 class="irie-cart-page__delivery-title">How would you like your order?</h3>
        <p class="irie-cart-page__delivery-copy">Choose your fulfilment method and preferred date below.</p>
      </div>`;
    return intro;
  };

  const enhanceCartPageWidget = () => {
    const footer = document.querySelector(footerSelector);
    const summary = footer?.querySelector(summarySelector);
    if (!footer || !summary) return;

    const widgetRoot = findPickeasyWidgetRoot(footer);
    const existingIntro = footer.querySelector('#IrieCartPageDeliveryIntro');

    if (!widgetRoot) {
      existingIntro?.remove();
      return;
    }

    decoratePickeasyWidget(widgetRoot);

    const intro = existingIntro || createDeliveryIntro();
    if (widgetRoot.previousElementSibling !== intro) widgetRoot.before(intro);

    if (!widgetRoot.dataset.irieCartPageBound) {
      widgetRoot.dataset.irieCartPageBound = 'true';
      widgetRoot.addEventListener('click', () => window.setTimeout(queueEnhancement, 40));
      widgetRoot.addEventListener('change', () => window.setTimeout(queueEnhancement, 40));
    }
  };

  const start = () => {
    queueEnhancement();
    window.setTimeout(queueEnhancement, 250);
    window.setTimeout(queueEnhancement, 1000);

    const observerTarget = document.getElementById('MainContent') || document.body;
    const observer = new MutationObserver(queueEnhancement);
    observer.observe(observerTarget, { childList: true, subtree: true });

    document.addEventListener('shopify:section:load', queueEnhancement);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const itemCount = event?.cartData?.item_count;
        const countElement = document.querySelector('.irie-cart-page__item-count');
        if (countElement && Number.isFinite(itemCount)) {
          countElement.textContent = `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;
        }
        queueEnhancement();
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
